import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ArrowDownIcon, ArrowUpIcon, BellIcon, CalendarClockIcon, CheckIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, CircleAlertIcon, MailIcon, MonitorSmartphoneIcon, PencilLineIcon, SparklesIcon, UsersIcon, VolumeXIcon } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { LoadingBlock } from '@/components/LoadingBlock';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Field } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { errorMessage } from '@/lib/api';
import { useDebounce } from '@/lib/hooks';
import { audienceSummary, browserTimeZone, CHANNEL_LABEL, describeMinutes, describeWindow, ROLE_LABEL, WINDOW_HINT, WINDOW_LABEL, type ReportCatalog, type ReportDryRun, type ReportsApi, type ReportSubscription, type ReportSubscriptionInput, type ReportWindowKind } from '@/lib/reports';
import { cn } from '@/lib/utils';
import { EmailChips } from './EmailChips';
import { PeoplePicker } from './PeoplePicker';
import { RecipientList } from './RecipientList';
import { ReportPayloadView } from './ReportPayloadView';
import { COOLDOWN_PRESETS, cronToSchedule, describeScheduleLocal, fmtInZone, scheduleToCron, suggestedWindow, type ScheduleModel } from './schedule';
import { ScheduleBuilder } from './ScheduleBuilder';
import { blankTemplate, templatesFor, type ReportTemplate } from './templates';
import { TimeZonePicker, type ZoneSuggestion } from './TimeZonePicker';

export interface EditorJob {
  id: string;
  name: string;
  timezone?: string | null;
}
export interface EditorPerson {
  id: string;
  name: string;
  email: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  api: ReportsApi;
  catalog: ReportCatalog;
  /** org: Super User editing organization or job reports. job: a job admin on one job. */
  scope: 'org' | 'job';
  jobId?: string;
  jobs?: EditorJob[];
  people?: EditorPerson[];
  /** Edit mode. */
  initial?: ReportSubscription | null;
  /** Create mode, pre-filled (a template key, or a draft built elsewhere such as the Overview page). */
  template?: string | Partial<ReportSubscriptionInput> | null;
  /** The organization's (or job's) zone; falls back to this computer's. */
  defaultTimezone?: string | null;
  onSave: (body: ReportSubscriptionInput) => Promise<ReportSubscription>;
  /** After a successful create: run it or send a test right away. */
  onCreated?: (sub: ReportSubscription, then: 'run' | 'test' | null) => void;
}

type Step = 'start' | 'what' | 'when' | 'who' | 'review';
const STEPS: Array<{ key: Exclude<Step, 'start'>; label: string; blurb: string }> = [
  { key: 'what', label: 'What', blurb: 'Name, coverage and sections' },
  { key: 'when', label: 'When', blurb: 'Schedule or watched events' },
  { key: 'who', label: 'Who', blurb: 'Audience and channels' },
  { key: 'review', label: 'Review', blurb: 'Check it before it goes out' },
];

interface State {
  name: string;
  description: string;
  enabled: boolean;
  jobId: string;
  sections: string[];
  filterJobIds: string[];
  format: 'summary' | 'detailed';
  attachCsv: boolean;
  triggerKind: 'schedule' | 'event';
  schedule: ScheduleModel;
  timezone: string;
  windowKind: ReportWindowKind;
  windowDays: string;
  windowTouched: boolean;
  eventTypes: string[];
  minCount: string;
  minRecipients: string;
  minFailedPct: string;
  outcomes: string[];
  cooldownMinutes: number;
  email: boolean;
  inApp: boolean;
  roles: string[];
  userIds: string[];
  emails: string[];
}

function fromInput(src: Partial<ReportSubscriptionInput> | ReportSubscription | null | undefined, ctx: { scope: 'org' | 'job'; jobId?: string; timezone: string }): State {
  const s = src ?? {};
  const cron = s.cron ?? '0 7 * * 1-5';
  const conditions = s.eventConditions ?? {};
  return {
    name: s.name ?? '',
    description: s.description ?? '',
    enabled: s.enabled ?? true,
    jobId: ctx.scope === 'job' ? (ctx.jobId ?? '') : (s.jobId ?? ''),
    sections: s.sections ?? [],
    filterJobIds: s.filters?.jobIds ?? [],
    format: s.format ?? 'summary',
    attachCsv: s.attachCsv ?? false,
    triggerKind: s.triggerKind ?? 'schedule',
    schedule: cronToSchedule(cron),
    timezone: s.timezone ?? ctx.timezone,
    windowKind: s.windowKind ?? 'since_last',
    windowDays: String(s.windowDays ?? 7),
    windowTouched: !!s.windowKind,
    eventTypes: s.eventTypes ?? [],
    minCount: String(conditions.minCount ?? 1),
    minRecipients: conditions.minRecipients !== undefined ? String(conditions.minRecipients) : '',
    minFailedPct: conditions.minFailedPct !== undefined ? String(conditions.minFailedPct) : '',
    outcomes: conditions.outcomes ?? [],
    cooldownMinutes: s.cooldownMinutes ?? 0,
    email: s.channels ? s.channels.includes('email') : true,
    inApp: s.channels ? s.channels.includes('in_app') : true,
    roles: s.recipients?.roles ?? [],
    userIds: s.recipients?.userIds ?? [],
    emails: s.recipients?.emails ?? [],
  };
}

function toInput(s: State, ctx: { scope: 'org' | 'job'; sectionKeys: Set<string>; eventKeys: Set<string>; roleKeys: Set<string> }): ReportSubscriptionInput {
  const isEvent = s.triggerKind === 'event';
  return {
    name: s.name.trim(),
    description: s.description.trim() || null,
    enabled: s.enabled,
    jobId: ctx.scope === 'org' ? s.jobId || null : undefined,
    sections: s.sections.filter((k) => ctx.sectionKeys.has(k)),
    filters: ctx.scope === 'org' && !s.jobId ? { jobIds: s.filterJobIds } : {},
    windowKind: s.windowKind,
    windowDays: s.windowKind === 'days' ? Math.max(1, Number(s.windowDays) || 7) : null,
    triggerKind: s.triggerKind,
    cron: isEvent ? null : scheduleToCron(s.schedule),
    timezone: s.timezone || 'UTC',
    eventTypes: isEvent ? s.eventTypes.filter((t) => ctx.eventKeys.has(t)) : [],
    eventConditions: isEvent
      ? {
          minCount: Math.max(1, Number(s.minCount) || 1),
          ...(s.minRecipients.trim() ? { minRecipients: Math.max(1, Number(s.minRecipients) || 1) } : {}),
          ...(s.minFailedPct.trim() ? { minFailedPct: Math.min(100, Math.max(0, Number(s.minFailedPct) || 0)) } : {}),
          ...(s.outcomes.length ? { outcomes: s.outcomes } : {}),
        }
      : {},
    cooldownMinutes: Math.max(0, s.cooldownMinutes | 0),
    channels: [...(s.email ? (['email'] as const) : []), ...(s.inApp ? (['in_app'] as const) : [])],
    recipients: { roles: s.roles.filter((r) => ctx.roleKeys.has(r)), userIds: s.userIds, emails: s.emails },
    format: s.format,
    attachCsv: s.attachCsv,
  };
}

const toggle = (list: string[], key: string, on: boolean) => (on ? [...new Set([...list, key])] : list.filter((k) => k !== key));
const move = (list: string[], from: number, to: number) => {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [x] = next.splice(from, 1);
  next.splice(to, 0, x as string);
  return next;
};

export function ReportEditor({ open, onClose, api, catalog, scope, jobId, jobs = [], people = [], initial, template, defaultTimezone, onSave, onCreated }: Props) {
  const isEdit = !!initial;
  const baseZone = defaultTimezone && defaultTimezone.trim() ? defaultTimezone : browserTimeZone();
  const templates = useMemo(() => templatesFor(scope), [scope]);

  const initialState = (): State => {
    if (initial) return fromInput(initial, { scope, jobId, timezone: baseZone });
    if (typeof template === 'string') {
      const t = templates.find((x) => x.key === template);
      if (t) return fromInput(t.input(scope), { scope, jobId, timezone: baseZone });
    } else if (template) return fromInput(template, { scope, jobId, timezone: baseZone });
    return fromInput(blankTemplate('schedule', scope), { scope, jobId, timezone: baseZone });
  };
  const [s, setS] = useState<State>(initialState);
  const [step, setStep] = useState<Step>(isEdit || template ? 'what' : 'start');
  const [attempted, setAttempted] = useState<Set<Step>>(new Set());
  const [saving, setSaving] = useState<null | 'save' | 'run' | 'test'>(null);
  const set = <K extends keyof State>(k: K, v: State[K]) => setS((p) => ({ ...p, [k]: v }));

  const jobLevel = scope === 'job' || !!s.jobId;
  const sections = useMemo(() => catalog.sections.filter((x) => !jobLevel || x.jobLevel).filter((x) => scope === 'org' || !x.adminOnly), [catalog, jobLevel, scope]);
  const events = useMemo(() => catalog.events.filter((x) => !jobLevel || x.jobLevel), [catalog, jobLevel]);
  const roles = useMemo(() => catalog.recipientRoles.filter((x) => !jobLevel || x.jobLevel), [catalog, jobLevel]);
  const sectionKeys = useMemo(() => new Set(sections.map((x) => x.key)), [sections]);
  const eventKeys = useMemo(() => new Set(events.map((x) => x.type)), [events]);
  const roleKeys = useMemo(() => new Set(roles.map((x) => x.key)), [roles]);
  const selectedSections = s.sections.filter((k) => sectionKeys.has(k));
  const watched = events.filter((e) => s.eventTypes.includes(e.type));
  const wants = (c: string) => watched.some((e) => e.conditions?.includes(c as never));
  const coveredJob = scope === 'job' ? null : jobs.find((j) => j.id === s.jobId) ?? null;

  const zoneSuggestions: ZoneSuggestion[] = useMemo(() => {
    const out: ZoneSuggestion[] = [];
    const jz = scope === 'job' ? jobs.find((j) => j.id === jobId)?.timezone : coveredJob?.timezone;
    if (jz) out.push({ id: jz, label: 'This job' });
    if (defaultTimezone) out.push({ id: defaultTimezone, label: scope === 'job' && !jz ? 'This job' : 'Your organization' });
    out.push({ id: browserTimeZone(), label: 'Your computer' });
    return out;
  }, [scope, jobs, jobId, coveredJob, defaultTimezone]);

  // Live schedule explanation (server-side cron parser), debounced while typing a custom expression.
  const cron = s.triggerKind === 'schedule' ? scheduleToCron(s.schedule) : '';
  const debouncedCron = useDebounce(cron, 350);
  const scheduleQ = useQuery({ queryKey: ['reports', 'schedule', debouncedCron, s.timezone], queryFn: () => api.schedule(debouncedCron, s.timezone, 3), enabled: s.triggerKind === 'schedule' && !!debouncedCron, staleTime: 60_000 });
  const scheduleInfo = scheduleQ.data?.schedule ?? null;
  const scheduleValid = s.triggerKind !== 'schedule' || (scheduleInfo && scheduleInfo.cron === cron.replace(/\s+/g, ' ').trim() ? scheduleInfo.valid : true);

  // Suggest a natural window when the cadence changes, until the person picks one deliberately.
  const cadenceKey = `${s.schedule.frequency}:${s.schedule.weekdays.join(',')}:${s.schedule.monthDays.join(',')}`;
  useEffect(() => {
    if (s.windowTouched || s.triggerKind !== 'schedule' || s.schedule.frequency === 'custom') return;
    const suggestion = suggestedWindow(s.schedule);
    setS((p) => (p.windowKind === suggestion ? p : { ...p, windowKind: suggestion }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cadenceKey]);

  // Validation per step.
  const problems: Record<Exclude<Step, 'start' | 'review'>, string[]> = {
    what: [...(s.name.trim() ? [] : ['Give the report a name.']), ...(s.triggerKind === 'schedule' && selectedSections.length === 0 ? ['Pick at least one section.'] : [])],
    when: [...(s.triggerKind === 'schedule' && !scheduleValid ? [scheduleInfo?.error ?? 'Fix the schedule.'] : []), ...(s.triggerKind === 'event' && s.eventTypes.filter((t) => eventKeys.has(t)).length === 0 ? ['Pick at least one event to watch.'] : []), ...(s.windowKind === 'days' && !(Number(s.windowDays) >= 1) ? ['Enter how many days the window covers.'] : [])],
    who: [...(s.roles.filter((r) => roleKeys.has(r)).length + s.userIds.length + s.emails.length === 0 ? ['Choose who receives it.'] : []), ...(!s.email && !s.inApp ? ['Turn on email, in-app or both.'] : [])],
  };
  const stepValid = (k: Step) => (k === 'what' || k === 'when' || k === 'who' ? problems[k].length === 0 : true);
  const allValid = stepValid('what') && stepValid('when') && stepValid('who');
  const body = useMemo(() => toInput(s, { scope, sectionKeys, eventKeys, roleKeys }), [s, scope, sectionKeys, eventKeys, roleKeys]);

  // Review: compute the draft for real (nothing stored), resolve the audience, explain the schedule.
  const dryRunKey = JSON.stringify(body);
  const dryRun = useQuery({ queryKey: ['reports', 'dry-run', initial?.id ?? 'new', dryRunKey], queryFn: () => api.dryRun(body, initial?.id), enabled: step === 'review' && allValid, staleTime: 30_000, retry: false });

  const order: Step[] = isEdit ? ['what', 'when', 'who', 'review'] : ['start', 'what', 'when', 'who', 'review'];
  const idx = order.indexOf(step);
  const goto = (next: Step) => {
    // Jumping forward past an invalid step marks it so its problems show.
    const marks = new Set(attempted);
    for (const k of order.slice(0, order.indexOf(next))) marks.add(k);
    setAttempted(marks);
    const blocker = order.slice(0, order.indexOf(next)).find((k) => !stepValid(k));
    if (blocker && order.indexOf(blocker) < order.indexOf(next) && blocker !== 'start') {
      setStep(blocker);
      return;
    }
    setStep(next);
  };
  const next = () => goto(order[Math.min(order.length - 1, idx + 1)] as Step);
  const back = () => setStep(order[Math.max(0, idx - 1)] as Step);

  const pickTemplate = (t: ReportTemplate | { kind: 'schedule' | 'event' }) => {
    const input = 'input' in t ? t.input(scope) : blankTemplate(t.kind, scope);
    setS(fromInput({ ...input, timezone: baseZone }, { scope, jobId, timezone: baseZone }));
    setStep('what');
  };

  const submit = async (then: 'run' | 'test' | null) => {
    setAttempted(new Set(order));
    if (!allValid) {
      const first = order.find((k) => !stepValid(k));
      if (first) setStep(first);
      toast.error('A few things still need attention.');
      return;
    }
    setSaving(then ?? 'save');
    try {
      const saved = await onSave(body);
      onClose();
      if (!isEdit) onCreated?.(saved, then);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(null);
    }
  };

  const showProblems = (k: Step) => attempted.has(k) && k !== 'start' && k !== 'review' && problems[k].length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[94vh] w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl" showCloseButton>
        {/* ------------------------------------------------------------ header + stepper */}
        <div className="border-b px-6 pt-5 pb-0">
          <div className="pr-8">
            <DialogTitle className="text-lg">{isEdit ? `Edit · ${initial?.name}` : 'New report'}</DialogTitle>
            <DialogDescription className="mt-1">{isEdit ? 'Changes apply from the next run. Every recipient can still mute it from their own account.' : step === 'start' ? 'Start from a ready-made report or from scratch. You can change everything before it goes out.' : 'Choose what goes in, when it goes out and who gets it, then check the result before saving.'}</DialogDescription>
          </div>
          {step !== 'start' && (
            <ol className="mt-4 flex gap-1 overflow-x-auto" aria-label="Steps">
              {STEPS.map((st, i) => {
                const active = step === st.key;
                const done = order.indexOf(st.key) < idx && stepValid(st.key);
                const broken = showProblems(st.key);
                return (
                  <li key={st.key} className="shrink-0">
                    <button type="button" onClick={() => goto(st.key)} aria-current={active ? 'step' : undefined} className={cn('flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm transition-colors', active ? 'border-primary text-foreground font-medium' : 'text-muted-foreground hover:text-foreground border-transparent')}>
                      <span className={cn('flex size-5 items-center justify-center text-[11px] font-semibold', broken ? 'bg-destructive text-white' : done ? 'bg-foreground text-background' : active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')} aria-hidden>
                        {broken ? '!' : done ? <CheckIcon className="size-3" /> : i + 1}
                      </span>
                      {st.label}
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {/* ------------------------------------------------------------ body */}
        <div className={cn('min-h-0 flex-1 overflow-y-auto', step !== 'start' && 'lg:grid lg:grid-cols-[minmax(0,1fr)_17.5rem]')}>
          <form
            className="space-y-7 p-6"
            onSubmit={(e) => {
              e.preventDefault();
              if (step === 'review') void submit(null);
              else next();
            }}
          >
            {step === 'start' && <StartStep templates={templates} onPick={pickTemplate} />}

            {step === 'what' && (
              <>
                {showProblems('what') && <Problems items={problems.what} />}
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Name" htmlFor="rp-name" className="sm:col-span-2">
                    <Input id="rp-name" value={s.name} onChange={(e) => set('name', e.target.value)} placeholder={s.triggerKind === 'schedule' ? 'Monday morning numbers' : 'Delivery problems'} autoFocus={!isEdit} maxLength={200} />
                  </Field>
                  <Field label="Description" htmlFor="rp-desc" description="Optional. Shown at the top of the email and in the app." className="sm:col-span-2">
                    <Textarea id="rp-desc" rows={2} value={s.description} onChange={(e) => set('description', e.target.value)} maxLength={1000} />
                  </Field>
                  {scope === 'org' && (
                    <Field label="Covers" htmlFor="rp-job" description={s.jobId ? 'A job report only offers job-level sections and audiences.' : 'Organization-wide reports can include people, help requests and billing.'}>
                      <Select
                        value={s.jobId || 'org'}
                        onValueChange={(v) => {
                          set('jobId', v === 'org' ? '' : v);
                          const jz = jobs.find((j) => j.id === v)?.timezone;
                          if (jz && !isEdit) set('timezone', jz);
                        }}
                      >
                        <SelectTrigger id="rp-job" className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="org">The whole organization</SelectItem>
                          {jobs.map((jb) => (
                            <SelectItem key={jb.id} value={jb.id}>
                              Job · {jb.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                  <Field label="Detail" htmlFor="rp-format" description={s.format === 'detailed' ? 'Numbers plus row-by-row tables (every message, every run).' : 'Headline numbers with short tables where they help.'}>
                    <Select value={s.format} onValueChange={(v) => set('format', v as State['format'])}>
                      <SelectTrigger id="rp-format" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="summary">Summary</SelectItem>
                        <SelectItem value="detailed">Detailed</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div>
                  <div className="mb-2 flex items-end justify-between gap-3">
                    <Label>
                      Sections {s.triggerKind === 'event' && <span className="text-muted-foreground font-normal">(optional for alerts: numbers since the last notice)</span>}
                    </Label>
                    <span className="text-muted-foreground text-xs">
                      {selectedSections.length} of {sections.length} · order them in the summary
                    </span>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {sections.map((d) => {
                      const on = selectedSections.includes(d.key);
                      const n = selectedSections.indexOf(d.key) + 1;
                      return (
                        <label key={d.key} className={cn('relative flex cursor-pointer items-start gap-3 border p-3 text-sm transition-colors', on ? 'border-primary bg-primary/5' : 'hover:bg-accent/40')}>
                          <Checkbox checked={on} onCheckedChange={(v) => set('sections', toggle(s.sections, d.key, v === true))} className="mt-0.5" />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-2 font-medium">
                              {d.title}
                              {d.adminOnly && <Badge variant="outline">Super Users</Badge>}
                            </span>
                            <span className="text-muted-foreground block text-xs">{d.description}</span>
                          </span>
                          {on && <span className="bg-primary text-primary-foreground absolute top-2 right-2 flex size-5 items-center justify-center text-[11px] font-semibold tabular-nums">{n}</span>}
                        </label>
                      );
                    })}
                  </div>
                </div>

                {scope === 'org' && !s.jobId && jobs.length > 1 && (
                  <div>
                    <Label className="mb-2 block">
                      Only these jobs <span className="text-muted-foreground font-normal">(leave empty for every job)</span>
                    </Label>
                    <div className="flex flex-wrap gap-1.5">
                      {jobs.map((jb) => {
                        const on = s.filterJobIds.includes(jb.id);
                        return (
                          <button key={jb.id} type="button" aria-pressed={on} onClick={() => set('filterJobIds', toggle(s.filterJobIds, jb.id, !on))} className={cn('border px-2.5 py-1 text-xs transition-colors', on ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-accent')}>
                            {jb.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}

            {step === 'when' && (
              <>
                {showProblems('when') && <Problems items={problems.when} />}
                <div className="grid gap-2 sm:grid-cols-2">
                  {(['schedule', 'event'] as const).map((k) => (
                    <label key={k} className={cn('flex cursor-pointer items-start gap-3 border p-3 text-sm transition-colors', s.triggerKind === k ? 'border-primary bg-primary/5' : 'hover:bg-accent/40')}>
                      <input type="radio" name="trigger" className="mt-1 accent-current" checked={s.triggerKind === k} onChange={() => set('triggerKind', k)} />
                      <span>
                        <span className="flex items-center gap-2 font-medium">
                          {k === 'schedule' ? <CalendarClockIcon className="size-4" aria-hidden /> : <BellIcon className="size-4" aria-hidden />}
                          {k === 'schedule' ? 'On a schedule' : 'When something happens'}
                        </span>
                        <span className="text-muted-foreground block text-xs">{k === 'schedule' ? 'A digest at a fixed time: daily, weekly, monthly or any cron expression.' : 'Watch for events and send a notice as they happen, batched the way you choose.'}</span>
                      </span>
                    </label>
                  ))}
                </div>

                {s.triggerKind === 'schedule' ? (
                  <>
                    <ScheduleBuilder value={s.schedule} onChange={(m) => set('schedule', m)} timezone={s.timezone} onTimezoneChange={(tz) => set('timezone', tz)} zoneSuggestions={zoneSuggestions} info={scheduleInfo && scheduleInfo.cron === cron.replace(/\s+/g, ' ').trim() ? scheduleInfo : null} infoLoading={scheduleQ.isFetching} />
                    <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
                      <Field label="Each run covers" htmlFor="rp-window" description={WINDOW_HINT[s.windowKind]}>
                        <Select
                          value={s.windowKind}
                          onValueChange={(v) => {
                            set('windowKind', v as ReportWindowKind);
                            set('windowTouched', true);
                          }}
                        >
                          <SelectTrigger id="rp-window" className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {catalog.windows.map((w) => (
                              <SelectItem key={w.key} value={w.key}>
                                {WINDOW_LABEL[w.key] ?? w.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      {s.windowKind === 'days' && (
                        <Field label="Days" htmlFor="rp-days">
                          <Input id="rp-days" inputMode="numeric" className="w-24" value={s.windowDays} onChange={(e) => set('windowDays', e.target.value)} />
                        </Field>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label className="mb-2 block">Events to watch</Label>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {events.map((e) => {
                          const on = s.eventTypes.includes(e.type);
                          return (
                            <div key={e.type} className={cn('border text-sm transition-colors', on ? 'border-primary bg-primary/5' : 'hover:bg-accent/40')}>
                              <label className="flex cursor-pointer items-start gap-3 p-3">
                                <Checkbox checked={on} onCheckedChange={(v) => set('eventTypes', toggle(s.eventTypes, e.type, v === true))} className="mt-0.5" />
                                <span>
                                  <span className="font-medium">{e.title}</span>
                                  <span className="text-muted-foreground block text-xs">{e.description}</span>
                                </span>
                              </label>
                              {on && e.conditions && e.conditions.length > 0 && (
                                <div className="border-primary/30 flex flex-wrap items-end gap-3 border-t px-3 py-2.5">
                                  {e.conditions.includes('minRecipients') && (
                                    <Field label="Only sends with at least" htmlFor={`rp-minrec-${e.type}`} description="recipients" className="gap-1">
                                      <Input id={`rp-minrec-${e.type}`} inputMode="numeric" className="h-8 w-24" value={s.minRecipients} onChange={(ev) => set('minRecipients', ev.target.value)} placeholder="any" />
                                    </Field>
                                  )}
                                  {e.conditions.includes('minFailedPct') && (
                                    <Field label="Only when failed share reaches" htmlFor={`rp-minfail-${e.type}`} description="percent" className="gap-1">
                                      <Input id={`rp-minfail-${e.type}`} inputMode="decimal" className="h-8 w-24" value={s.minFailedPct} onChange={(ev) => set('minFailedPct', ev.target.value)} placeholder="any" />
                                    </Field>
                                  )}
                                  {e.conditions.includes('minCount') && (
                                    <Field label="Notify once at least" htmlFor={`rp-mincount-${e.type}`} description="events are waiting" className="gap-1">
                                      <Input id={`rp-mincount-${e.type}`} inputMode="numeric" className="h-8 w-24" value={s.minCount} onChange={(ev) => set('minCount', ev.target.value)} />
                                    </Field>
                                  )}
                                  {e.conditions.includes('outcomes') && (
                                    <div className="grid gap-1">
                                      <Label className="text-xs">Outcomes</Label>
                                      <div className="flex flex-wrap gap-1.5">
                                        {['sent', 'drafted', 'skipped', 'failed'].map((o) => {
                                          const picked = s.outcomes.includes(o);
                                          return (
                                            <button key={o} type="button" aria-pressed={picked} onClick={() => set('outcomes', toggle(s.outcomes, o, !picked))} className={cn('border px-2 py-0.5 text-xs capitalize transition-colors', picked ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-accent')}>
                                              {o}
                                            </button>
                                          );
                                        })}
                                      </div>
                                      <span className="text-muted-foreground text-xs">{s.outcomes.length === 0 ? 'All outcomes.' : `Only ${s.outcomes.join(', ')}.`}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <Label className="mb-2 block">Batching</Label>
                        <div className="inline-flex flex-wrap border" role="radiogroup" aria-label="Cooldown">
                          {COOLDOWN_PRESETS.map((p) => (
                            <button key={p.minutes} type="button" role="radio" aria-checked={s.cooldownMinutes === p.minutes} onClick={() => set('cooldownMinutes', p.minutes)} className={cn('px-3 py-1.5 text-sm transition-colors', s.cooldownMinutes === p.minutes ? 'bg-foreground text-background font-medium' : 'text-muted-foreground hover:bg-accent hover:text-foreground')}>
                              {p.label}
                            </button>
                          ))}
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <Input inputMode="numeric" className="h-8 w-24" aria-label="Cooldown in minutes" value={String(s.cooldownMinutes)} onChange={(e) => set('cooldownMinutes', Math.max(0, Math.min(10_080, Number(e.target.value) || 0)))} />
                          <span className="text-muted-foreground text-xs">minutes between notices · {COOLDOWN_PRESETS.find((p) => p.minutes === s.cooldownMinutes)?.hint ?? `At most one notice every ${describeMinutes(s.cooldownMinutes)}.`}</span>
                        </div>
                      </div>
                      {!wants('minCount') && (
                        <Field label="Wait until at least" htmlFor="rp-mincount" description="events are waiting before a notice goes out (1 = every time).">
                          <Input id="rp-mincount" inputMode="numeric" className="w-24" value={s.minCount} onChange={(e) => set('minCount', e.target.value)} />
                        </Field>
                      )}
                      <Field label="Time zone for timestamps" htmlFor="rp-tz-ev" className="sm:col-span-2">
                        <TimeZonePicker id="rp-tz-ev" value={s.timezone} onChange={(tz) => set('timezone', tz)} suggestions={zoneSuggestions} />
                      </Field>
                    </div>
                    <p className="text-muted-foreground text-xs">Watches are checked about once a minute. Sections you selected under “What” ride along on every notice with numbers for the period since the last one.</p>
                  </>
                )}
              </>
            )}

            {step === 'who' && (
              <>
                {showProblems('who') && <Problems items={problems.who} />}
                <div>
                  <Label className="mb-2 block">Audience</Label>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {roles.map((r) => {
                      const on = s.roles.includes(r.key);
                      return (
                        <label key={r.key} className={cn('flex cursor-pointer items-start gap-3 border p-3 text-sm transition-colors', on ? 'border-primary bg-primary/5' : 'hover:bg-accent/40')}>
                          <Checkbox checked={on} onCheckedChange={(v) => set('roles', toggle(s.roles, r.key, v === true))} className="mt-0.5" />
                          <span>
                            <span className="font-medium">{r.label}</span>
                            <span className="text-muted-foreground block text-xs">{r.description}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Specific people" htmlFor="rp-people" description={people.length === 0 ? 'People show up here once they have accepted their invitation.' : 'Anyone in the organization, whatever their level.'}>
                    <PeoplePicker id="rp-people" options={people.map((p) => ({ id: p.id, name: p.name, hint: p.email }))} value={s.userIds} onChange={(ids) => set('userIds', ids)} />
                  </Field>
                  <Field label="Other email addresses" htmlFor="rp-emails" description="Email only, no in-app copy. Each address gets its own unsubscribe link.">
                    <EmailChips id="rp-emails" value={s.emails} onChange={(v) => set('emails', v)} />
                  </Field>
                </div>
                <div>
                  <Label className="mb-2 block">How it arrives</Label>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <ToggleCard icon={<MailIcon className="size-4" aria-hidden />} title="Email" hint="The full report, with a one-click unsubscribe in the footer." checked={s.email} onChange={(v) => set('email', v)} />
                    <ToggleCard icon={<MonitorSmartphoneIcon className="size-4" aria-hidden />} title="In the app" hint="Under Reports, with an unread badge." checked={s.inApp} onChange={(v) => set('inApp', v)} />
                    <ToggleCard icon={<SparklesIcon className="size-4" aria-hidden />} title="Attach a CSV" hint="Every table in one spreadsheet-friendly file (email only)." checked={s.attachCsv} onChange={(v) => set('attachCsv', v)} disabled={!s.email} />
                    <ToggleCard icon={<CheckIcon className="size-4" aria-hidden />} title="Active" hint="Paused reports keep their settings but never run." checked={s.enabled} onChange={(v) => set('enabled', v)} />
                  </div>
                </div>
                <p className="text-muted-foreground text-xs">Everyone can turn report emails or in-app copies off for themselves, or mute one report, under Account → Notifications. The next step shows who will actually receive this one.</p>
              </>
            )}

            {step === 'review' && (
              <ReviewStep
                s={s}
                body={body}
                scope={scope}
                coveredJob={coveredJob}
                sections={sections}
                events={events}
                jobs={jobs}
                scheduleText={s.triggerKind === 'schedule' ? (scheduleInfo?.valid ? scheduleInfo.description : null) ?? describeScheduleLocal(s.schedule) : null}
                dryRun={dryRun}
                allValid={allValid}
                problems={[...problems.what, ...problems.when, ...problems.who]}
                onEdit={setStep}
              />
            )}
            {/* Enter submits the current step. */}
            <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
          </form>

          {step !== 'start' && (
            <aside className="bg-muted/30 hidden border-l p-5 lg:block">
              <SummaryRail s={s} scope={scope} coveredJob={coveredJob} sections={sections} scheduleText={s.triggerKind === 'schedule' ? (scheduleInfo?.valid ? scheduleInfo.description : null) ?? describeScheduleLocal(s.schedule) : `${s.eventTypes.filter((t) => eventKeys.has(t)).length} events watched`} nextRun={scheduleInfo?.valid ? scheduleInfo.nextRuns[0] ?? null : null} onReorder={(from, to) => set('sections', move(selectedSections, from, to))} onJump={setStep} />
            </aside>
          )}
        </div>

        {/* ------------------------------------------------------------ footer */}
        <div className="flex items-center justify-between gap-3 border-t px-6 py-4">
          <div className="flex items-center gap-2">
            {step !== 'start' && idx > (isEdit ? 0 : 1) && (
              <Button type="button" variant="ghost" onClick={back}>
                <ChevronLeftIcon aria-hidden /> Back
              </Button>
            )}
            {step !== 'start' && !isEdit && idx === 1 && (
              <Button type="button" variant="ghost" onClick={() => setStep('start')}>
                <ChevronLeftIcon aria-hidden /> Templates
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            {step !== 'start' && step !== 'review' && (
              <Button type="button" onClick={next}>
                Next <ChevronRightIcon aria-hidden />
              </Button>
            )}
            {step === 'review' &&
              (isEdit ? (
                <Button type="button" disabled={!!saving} onClick={() => void submit(null)}>
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
              ) : (
                <div className="flex">
                  <Button type="button" className="rounded-r-none" disabled={!!saving} onClick={() => void submit(null)}>
                    {saving === 'save' ? 'Creating…' : 'Create report'}
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" className="border-primary-foreground/20 rounded-l-none border-l px-2" disabled={!!saving} aria-label="More ways to create">
                        <ChevronDownIcon aria-hidden />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => void submit('run')}>Create and run now</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => void submit('test')}>Create and send me a test</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- pieces

function Problems({ items }: { items: string[] }) {
  return (
    <div role="alert" className="border-destructive/40 bg-destructive/5 text-destructive flex items-start gap-2.5 border px-3 py-2.5 text-sm">
      <CircleAlertIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <ul className="space-y-0.5">
        {items.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
    </div>
  );
}

function ToggleCard({ icon, title, hint, checked, onChange, disabled }: { icon: ReactNode; title: string; hint: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label className={cn('flex items-center justify-between gap-3 border p-3 text-sm', checked && !disabled && 'border-primary bg-primary/5', disabled && 'opacity-60')}>
      <span className="flex items-start gap-2.5">
        <span className="text-muted-foreground mt-0.5">{icon}</span>
        <span>
          <span className="font-medium">{title}</span>
          <span className="text-muted-foreground block text-xs">{hint}</span>
        </span>
      </span>
      <Switch checked={checked && !disabled} onCheckedChange={onChange} disabled={disabled} aria-label={title} />
    </label>
  );
}

function TemplateCard({ t, onPick }: { t: ReportTemplate; onPick: (t: ReportTemplate) => void }) {
  return (
    <button type="button" onClick={() => onPick(t)} className="group hover:border-primary hover:bg-primary/5 focus-visible:ring-ring/50 flex h-full flex-col items-start gap-2 border p-4 text-left transition-colors focus-visible:ring-[3px] focus-visible:outline-none">
      <span className="bg-muted text-muted-foreground group-hover:bg-primary group-hover:text-primary-foreground flex size-8 items-center justify-center transition-colors">
        <t.icon className="size-4" aria-hidden />
      </span>
      <span className="text-sm font-medium">{t.title}</span>
      <span className="text-muted-foreground text-xs leading-relaxed">{t.blurb}</span>
    </button>
  );
}

function StartStep({ templates, onPick }: { templates: ReportTemplate[]; onPick: (t: ReportTemplate | { kind: 'schedule' | 'event' }) => void }) {
  const digests = templates.filter((t) => t.kind === 'schedule');
  const alerts = templates.filter((t) => t.kind === 'event');
  return (
    <div className="space-y-7">
      <section>
        <h3 className="mb-1 text-sm font-semibold">Digests on a schedule</h3>
        <p className="text-muted-foreground mb-3 text-xs">A fixed time, a fixed period, the same audience every time.</p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {digests.map((t) => (
            <TemplateCard key={t.key} t={t} onPick={onPick} />
          ))}
        </div>
      </section>
      <section>
        <h3 className="mb-1 text-sm font-semibold">Alerts when something happens</h3>
        <p className="text-muted-foreground mb-3 text-xs">Watch for events and get a notice, right away or batched.</p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {alerts.map((t) => (
            <TemplateCard key={t.key} t={t} onPick={onPick} />
          ))}
        </div>
      </section>
      <section className="flex flex-wrap items-center gap-2 border-t pt-5">
        <span className="text-muted-foreground mr-1 text-sm">Or start from scratch:</span>
        <Button type="button" variant="outline" size="sm" onClick={() => onPick({ kind: 'schedule' })}>
          <CalendarClockIcon aria-hidden /> Blank digest
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => onPick({ kind: 'event' })}>
          <BellIcon aria-hidden /> Blank alert
        </Button>
      </section>
    </div>
  );
}

function RailRow({ step, title, onJump, children }: { step: Step; title: string; onJump: (step: Step) => void; children: ReactNode }) {
  return (
    <div className="group">
      <button type="button" onClick={() => onJump(step)} className="text-muted-foreground hover:text-foreground flex w-full items-center justify-between text-[11px] font-semibold tracking-wide uppercase">
        {title}
        <PencilLineIcon className="size-3 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
      </button>
      <div className="mt-1 text-sm">{children}</div>
    </div>
  );
}

function SummaryRail({ s, scope, coveredJob, sections, scheduleText, nextRun, onReorder, onJump }: { s: State; scope: 'org' | 'job'; coveredJob: EditorJob | null; sections: ReportCatalog['sections']; scheduleText: string; nextRun: string | null; onReorder: (from: number, to: number) => void; onJump: (step: Step) => void }) {
  const selected = s.sections.filter((k) => sections.some((d) => d.key === k));
  const Row = ({ step, title, children }: { step: Step; title: string; children: ReactNode }) => (
    <RailRow step={step} title={title} onJump={onJump}>
      {children}
    </RailRow>
  );
  return (
    <div className="space-y-5">
      <Row step="what" title="Report">
        <div className="font-medium">{s.name.trim() || <span className="text-muted-foreground italic">Untitled</span>}</div>
        <div className="text-muted-foreground text-xs">
          {scope === 'job' ? 'This job' : coveredJob ? `Job · ${coveredJob.name}` : 'Whole organization'}
          {scope === 'org' && !coveredJob && s.filterJobIds.length > 0 ? ` · ${s.filterJobIds.length} jobs` : ''} · {s.format}
        </div>
      </Row>
      <Row step="what" title={`Sections · ${selected.length}`}>
        {selected.length === 0 ? (
          <span className="text-muted-foreground text-xs">{s.triggerKind === 'event' ? 'Events only.' : 'None yet.'}</span>
        ) : (
          <ol className="space-y-1">
            {selected.map((k, i) => (
              <li key={k} className="bg-background flex items-center gap-1 border px-2 py-1 text-xs">
                <span className="text-muted-foreground w-4 tabular-nums">{i + 1}</span>
                <span className="min-w-0 flex-1 truncate">{sections.find((d) => d.key === k)?.title ?? k}</span>
                <button type="button" aria-label="Move up" disabled={i === 0} onClick={() => onReorder(i, i - 1)} className="text-muted-foreground hover:text-foreground disabled:opacity-30">
                  <ArrowUpIcon className="size-3" />
                </button>
                <button type="button" aria-label="Move down" disabled={i === selected.length - 1} onClick={() => onReorder(i, i + 1)} className="text-muted-foreground hover:text-foreground disabled:opacity-30">
                  <ArrowDownIcon className="size-3" />
                </button>
              </li>
            ))}
          </ol>
        )}
      </Row>
      <Row step="when" title="When">
        <div>{scheduleText}</div>
        <div className="text-muted-foreground text-xs">
          {s.timezone}
          {s.triggerKind === 'schedule' && ` · ${describeWindow(s.windowKind, Number(s.windowDays))}`}
          {s.triggerKind === 'event' && s.cooldownMinutes > 0 && ` · at most every ${describeMinutes(s.cooldownMinutes)}`}
        </div>
        {nextRun && s.triggerKind === 'schedule' && <div className="text-muted-foreground text-xs">Next: {fmtInZone(nextRun, s.timezone)}</div>}
      </Row>
      <Row step="who" title="Who">
        <div>{audienceSummary({ roles: s.roles, userIds: s.userIds, emails: s.emails })}</div>
        <div className="text-muted-foreground text-xs">{[s.email && 'email', s.inApp && 'in-app', s.attachCsv && s.email && 'CSV'].filter(Boolean).join(' · ') || 'no channel'}</div>
      </Row>
      {!s.enabled && <Badge variant="warning">Paused</Badge>}
    </div>
  );
}

function ReviewBlock({ step, title, icon, onEdit, children }: { step: Step; title: string; icon: ReactNode; onEdit: (step: Step) => void; children: ReactNode }) {
  return (
    <div className="border p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <span className="text-muted-foreground">{icon}</span>
          {title}
        </h3>
        <Button type="button" variant="ghost" size="xs" onClick={() => onEdit(step)}>
          <PencilLineIcon aria-hidden /> Edit
        </Button>
      </div>
      <div className="space-y-1 text-sm">{children}</div>
    </div>
  );
}

function ReviewStep({ s, body, scope, coveredJob, sections, events, jobs, scheduleText, dryRun, allValid, problems, onEdit }: { s: State; body: ReportSubscriptionInput; scope: 'org' | 'job'; coveredJob: EditorJob | null; sections: ReportCatalog['sections']; events: ReportCatalog['events']; jobs: EditorJob[]; scheduleText: string | null; dryRun: UseQueryResult<ReportDryRun>; allValid: boolean; problems: string[]; onEdit: (step: Step) => void }) {
  const selected = (body.sections ?? []).map((k) => sections.find((d) => d.key === k)?.title ?? k);
  const watched = (body.eventTypes ?? []).map((t) => events.find((e) => e.type === t)?.title ?? t);
  const Block = ({ step, title, icon, children }: { step: Step; title: string; icon: ReactNode; children: ReactNode }) => (
    <ReviewBlock step={step} title={title} icon={icon} onEdit={onEdit}>
      {children}
    </ReviewBlock>
  );
  const d = dryRun.data;
  const reach = d?.recipients.filter((r) => r.channels.length > 0) ?? [];
  return (
    <div className="space-y-6">
      {!allValid && <Problems items={problems} />}
      <div className="grid gap-3 sm:grid-cols-2">
        <Block step="what" title={s.name.trim() || 'Untitled report'} icon={<SparklesIcon className="size-4" aria-hidden />}>
          {s.description.trim() && <p className="text-muted-foreground">{s.description.trim()}</p>}
          <p>
            <span className="text-muted-foreground">Covers:</span> {scope === 'job' ? 'this job' : coveredJob ? `job “${coveredJob.name}”` : s.filterJobIds.length > 0 ? `${s.filterJobIds.length} of ${jobs.length} jobs` : 'the whole organization'} · {s.format}
          </p>
          <p>
            <span className="text-muted-foreground">Sections:</span> {selected.length ? selected.join(', ') : s.triggerKind === 'event' ? 'none (events only)' : 'none'}
          </p>
        </Block>
        <Block step="when" title="When" icon={s.triggerKind === 'schedule' ? <CalendarClockIcon className="size-4" aria-hidden /> : <BellIcon className="size-4" aria-hidden />}>
          {s.triggerKind === 'schedule' ? (
            <>
              <p>{scheduleText}</p>
              <p className="text-muted-foreground">
                {s.timezone} · each run covers: {describeWindow(s.windowKind, Number(s.windowDays))}
              </p>
              {d?.schedule?.valid && d.schedule.nextRuns.length > 0 && (
                <p className="text-muted-foreground text-xs">
                  Next: {d.schedule.nextRuns.map((n) => fmtInZone(n, s.timezone)).join(' · ')}
                </p>
              )}
            </>
          ) : (
            <>
              <p>{watched.join(', ') || 'No events picked'}</p>
              <p className="text-muted-foreground">
                {s.cooldownMinutes > 0 ? `At most one notice every ${describeMinutes(s.cooldownMinutes)}` : 'A notice on the next check'}
                {Number(s.minCount) > 1 ? ` · once ${s.minCount} events are waiting` : ''} · {s.timezone}
              </p>
              {d?.payload.events && <p className="text-muted-foreground text-xs">The last 7 days would have produced {d.payload.events.total} matching {d.payload.events.total === 1 ? 'event' : 'events'}.</p>}
            </>
          )}
        </Block>
        <Block step="who" title="Who" icon={<UsersIcon className="size-4" aria-hidden />}>
          <p>{audienceSummary({ roles: s.roles, userIds: s.userIds, emails: s.emails })}</p>
          <p className="text-muted-foreground">{[s.email && CHANNEL_LABEL.email, s.inApp && CHANNEL_LABEL.in_app, s.attachCsv && s.email && 'CSV attached'].filter(Boolean).join(' · ')}</p>
          {s.roles.length > 0 && <p className="text-muted-foreground text-xs">{s.roles.map((r) => ROLE_LABEL[r] ?? r).join(', ')} are resolved each time it runs, so new people are included automatically.</p>}
        </Block>
        <div className="border p-4">
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
            <UsersIcon className="text-muted-foreground size-4" aria-hidden /> Who gets it today
            {d && <Badge variant={reach.length > 0 ? 'secondary' : 'warning'}>{reach.length}</Badge>}
          </h3>
          {!allValid ? (
            <p className="text-muted-foreground text-sm">Fix the items above to see the audience.</p>
          ) : dryRun.isPending ? (
            <LoadingBlock rows={2} />
          ) : dryRun.isError ? (
            <p className="text-destructive text-sm">{errorMessage(dryRun.error)}</p>
          ) : d ? (
            <RecipientList recipients={d.recipients} />
          ) : null}
        </div>
      </div>

      <section className="border">
        <div className="flex items-center justify-between gap-3 border-b px-4 py-2.5">
          <h3 className="text-sm font-semibold">Preview</h3>
          <span className="text-muted-foreground text-xs">{d ? `Computed just now for ${d.payload.window.label}. Nothing was sent.` : 'What the first run would look like.'}</span>
        </div>
        <div className="p-4">
          {!allValid ? <p className="text-muted-foreground text-sm">Fix the items above to preview the report.</p> : dryRun.isPending ? <LoadingBlock rows={5} /> : dryRun.isError ? <p className="text-destructive text-sm">{errorMessage(dryRun.error)}</p> : d ? <ReportPayloadView payload={d.payload} compact /> : null}
        </div>
      </section>
      <p className="text-muted-foreground flex items-start gap-2 text-xs">
        <VolumeXIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        Scheduled runs with nothing to say are recorded as a quiet window and not emailed. Anyone can mute this report from Account → Notifications or the email footer.
      </p>
    </div>
  );
}
